import { useEffect } from 'react';
import { AlertTriangle, Info, CheckCircle } from 'lucide-react';

interface ConfirmModalProps {
  open: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'info' | 'success';
  loading?: boolean;
}

export function ConfirmModal({
  open,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'info',
  loading = false,
}: ConfirmModalProps) {

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  const icons = {
    danger: <AlertTriangle className="h-6 w-6 text-white" />,
    info: <Info className="h-6 w-6 text-white" />,
    success: <CheckCircle className="h-6 w-6 text-white" />,
  };

  const btnColors = {
    danger: 'bg-white text-red-600 hover:bg-red-100',
    info: 'bg-white text-blue-600 hover:bg-blue-100',
    success: 'bg-white text-green-600 hover:bg-green-100',
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">

      {/* Backdrop – NO CLICK CLOSE */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal Card */}
      <div
        className="relative z-10 w-full max-w-md mx-4 bg-blue-600 text-white rounded-2xl shadow-2xl p-6 animate-in fade-in zoom-in-95"
      >

        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          {icons[variant]}
          <div>
            <h3 className="text-lg font-semibold">{title}</h3>
            <p className="text-sm text-blue-100 mt-1">
              {message}
            </p>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-3 mt-6">

          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium bg-white/20 rounded-lg hover:bg-white/30 transition disabled:opacity-50"
          >
            {cancelText}
          </button>

          <button
            onClick={onConfirm}
            disabled={loading}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition disabled:opacity-50 ${btnColors[variant]}`}
          >
            {loading ? 'Processing...' : confirmText}
          </button>

        </div>
      </div>
    </div>
  );
}

/* ========================== */
/* ALERT MODAL (CENTERED BLUE) */
/* ========================== */

interface AlertModalProps {
  open: boolean;
  title: string;
  message: string;
  onClose: () => void;
  variant?: 'danger' | 'info' | 'success';
}

export function AlertModal({
  open,
  title,
  message,
  onClose,
  variant = 'info'
}: AlertModalProps) {

  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  const icons = {
    danger: <AlertTriangle className="h-6 w-6 text-white" />,
    info: <Info className="h-6 w-6 text-white" />,
    success: <CheckCircle className="h-6 w-6 text-white" />,
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">

      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      <div className="relative z-10 w-full max-w-md mx-4 bg-blue-600 text-white rounded-2xl shadow-2xl p-6">

        <div className="flex items-start gap-3 mb-4">
          {icons[variant]}
          <div>
            <h3 className="text-lg font-semibold">{title}</h3>
            <p className="text-sm text-blue-100 mt-1 whitespace-pre-line">
              {message}
            </p>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-medium bg-white text-blue-600 rounded-lg hover:bg-blue-100 transition"
          >
            OK
          </button>
        </div>

      </div>
    </div>
  );
}